import React from 'react';
import Product from '../components/Product';
import Variants from '../components/CheckoutForm/components/Variants';
import CheckoutForm from '../components/CheckoutForm';
import useCheckoutState from '../hooks/useCheckoutState';
import { checkHasEventModules, checkHasProductModules } from '../enums/modules';
import useCartCost from '../hooks/useCartCost';
import clsx from 'clsx';
import Button from '../components/Button';
import PoweredByStripe from '../components/PoweredByStripe/PoweredByStripe';
import CheckoutConfirmation from '../CheckoutConfirmation';
import CheckoutFormContainer from '../components/CheckoutFormContainer';
import isHexColor from '../lib/is-hex-color';
import { StoreNameWithLabel } from '../components/Product/Product';
import Media from '../components/Media';
import { PAGE_TYPES } from '../enums/page-types';
import { getMedia } from '../lib/getMedia';
import EventDetails from '../components/EventDetails';
import TicketGroups from '../components/TicketGroups';
import checkHasVisibleVariants from '../lib/checkHasVisibleVariants';
import Divider from '../components/Divider';
import getUrlFromCdn, { CDNFitOption } from '../services/getUrlFromCdn';

export default function CheckoutSplit() {
  const {
    checkoutType,
    checkout,
    product,
    seller,
    query,
    form,
    isPreview,
    isScreenshot,
    isConfirmation,
  } = useCheckoutState();
  const { productPriceAmount, setupFeeAmount } = useCartCost();

  const hasVisibleVariants = checkHasVisibleVariants(
    product.variants,
    form.variants,
    query,
    isPreview,
  );

  const hasProductModules = checkHasProductModules({
    checkout,
    seller,
    query,
    isScreenshot,
    productPriceAmount,
    setupFeeAmount,
    discountedFromPrice: product.discountedFromPrice,
    trialPeriodDays: product.trialPeriodDays,
    startDate: product.startDate,
  });

  const hasEventModules = checkHasEventModules({ checkout, seller });
  const hasTicketTypes = checkout.type === PAGE_TYPES.EVENT;

  const media = getMedia({ checkout, product });
  const hasMedia = media && media.length > 0;

  const defaultWidth = 1920;
  const defaultHeight = 1080;

  return (
    <div
      className={clsx(
        'h-full',
        checkout.formPosition === 'right'
          ? `md:bg-gradient-to-r from-[var(--checkout-bg)] from-50% to-white to-50% `
          : `md:bg-gradient-to-l from-[var(--checkout-bg)] from-50% to-white to-50%`,
      )}
      style={{
        '--checkout-bg': checkout.backgroundColor,
      }}
    >
      {checkout.background === 'image' && checkout.backgroundImage?.url && (
        <div
          className="fixed top-0 bottom-0 w-1/2 md:block hidden"
          style={{
            left: checkout.formPosition === 'left' ? '50%' : '0%',
            backgroundImage: `image-set(url("${getUrlFromCdn(checkout.backgroundImage.url, {
              width: defaultWidth,
              height: defaultHeight,
              fit: CDNFitOption.cover,
            })}"), url("${getUrlFromCdn(checkout.backgroundImage.url, {
              width: defaultWidth * 2,
              height: defaultHeight * 2,
              fit: CDNFitOption.cover,
            })}") 2x)`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
      )}

      {checkout.background !== 'image' && checkout.background === 'color' && (
        <div
          className={clsx(
            'fixed h-screen w-screen inset-0 bg-fixed bg-no-repeat bg-cover bg-center -z-[1]',
            'md:hidden block',
          )}
          style={{
            backgroundColor: checkout.backgroundColor,
          }}
        />
      )}

      <div className="mx-auto max-w-screen-xl">
        <div
          className={clsx(
            'cp_checkout cp_checkout-split min-h-screen overflow-auto md:grid md:grid-cols-2',
            {
              'my-8 px-4 md:px-0 md:my-0': checkoutType !== 'embed',
            },
          )}
        >
          <div
            className={clsx('md:block hidden', {
              'order-2': checkout.formPosition === 'left',
              'order-1': checkout.formPosition === 'right',
            })}
          />

          <div
            className={clsx('md:bg-white', {
              'order-1': checkout.formPosition === 'left',
              'order-2': checkout.formPosition === 'right',
            })}
          >
            {isConfirmation && (
              <div
                className={clsx(
                  'cp_card mx-auto w-full bg-white relative z-10 rounded shadow max-w-screen-xs',
                  'md:mx-auto md:px-4 md:pb-12 md:pt-12 md:pt-20 md:max-w-screen-xs md:shadow-none',
                )}
              >
                <CheckoutConfirmation />
              </div>
            )}

            {!isConfirmation && (
              <div
                className={clsx(
                  'cp_card mx-auto w-full bg-white relative z-10 rounded shadow max-w-screen-xs',
                  'md:mx-auto md:px-4 md:pb-12 md:pt-12 md:pt-20 md:max-w-screen-xs md:shadow-none',
                )}
              >
                <CheckoutFormContainer>
                  <div className="md:block hidden">
                    <StoreNameWithLabel />
                  </div>

                  {hasMedia && (
                    <div
                      className={clsx('cp_product-image-wrapper space-y-4 mb-4', 'px-4 md:px-0')}
                    >
                      <Media media={media} />
                    </div>
                  )}

                  {hasProductModules && (
                    <div className="px-4 md:px-0">
                      <Product />
                    </div>
                  )}

                  {hasEventModules && (
                    <div className="px-4 md:px-0">
                      <EventDetails />
                    </div>
                  )}

                  {hasTicketTypes && (
                    <div className="cp_ticket-types-wrapper mt-4 -mx-4 mb-4 md:px-0">
                      <TicketGroups />
                    </div>
                  )}

                  {hasVisibleVariants && (
                    <div className="cp_variants-wrapper mt-4 -mx-4 mb-4 md:px-0">
                      <Variants />
                    </div>
                  )}

                  {!hasVisibleVariants && hasProductModules && <Divider padded />}

                  <div className="px-4 md:px-0">
                    <CheckoutForm />
                  </div>
                </CheckoutFormContainer>

                {(checkout.type === PAGE_TYPES.CHECKOUT || checkout.type === PAGE_TYPES.EVENT) &&
                  checkout.showPoweredByStripe && (
                    <div className="cp_powered-by-stripe-wrapper w-full flex justify-center">
                      <Button
                        to="https://stripe.com"
                        target="_blank"
                        appearance="bare"
                        className="cp_powered-by-stripe-link"
                      >
                        <PoweredByStripe style={checkout.poweredByStripeStyle} />
                      </Button>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
